import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const ActivationFunnel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    fetchFunnelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFunnelData = async () => {
    try {
      const response = await fetchWithAuth('/api/analytics/funnel');
      if (!response.ok) throw new Error('Failed to fetch funnel data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading activation funnel...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return null;

  const { funnel, metrics, conversionBySource, weeklyCohorts } = data;

  const funnelSteps = [
    { 
      label: 'Total Signups', 
      value: funnel.totalUsers, 
      color: '#6366f1',
      description: 'All users who downloaded the app'
    },
    { 
      label: 'Set Fitness Goal', 
      value: funnel.usersWithGoals, 
      color: '#8b5cf6',
      description: 'Completed onboarding with a goal'
    },
    { 
      label: 'First Food Log', 
      value: funnel.usersWithFoodLog, 
      color: '#a855f7',
      description: 'Logged at least one meal'
    },
    { 
      label: 'Activated (3+ logs/week)', 
      value: funnel.activatedUsers, 
      color: '#d946ef',
      description: '3+ food logs in first 7 days'
    },
    { 
      label: 'Returned Week 2', 
      value: funnel.returnedWeek2, 
      color: '#ec4899',
      description: 'Active during days 7-14'
    },
    { 
      label: 'Power Users (7+ streak)', 
      value: funnel.powerUsers, 
      color: '#f43f5e',
      description: 'Achieved 7+ day logging streak'
    }
  ];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
        🔄 Activation Funnel
      </h1>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '16px' }}>
        Track user journey from signup to engaged power user
      </p>

      {/* Key Metrics Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Activation Rate</div>
          <div className="stat-value" style={{ color: 'white' }}>{metrics.activationRate}%</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            3+ logs in first week
          </div>
        </div>

        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Week 2 Retention</div>
          <div className="stat-value" style={{ color: 'white' }}>{metrics.week2RetentionRate}%</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            Of users who logged food
          </div>
        </div>

        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Time to First Log</div>
          <div className="stat-value" style={{ color: 'white' }}>{metrics.avgHoursToFirstLog}h</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            Average hours after signup
          </div>
        </div>

        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Power User Rate</div>
          <div className="stat-value" style={{ color: 'white' }}>{metrics.powerUserRate}%</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            7+ day streak achieved
          </div>
        </div>
      </div>

      {/* Visual Funnel */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title">📊 User Journey Funnel</h2>
        <div style={{ padding: '20px 0' }}>
          {funnelSteps.map((step, index) => {
            const widthPercent = (step.value / funnelSteps[0].value) * 100;
            const prevStep = index > 0 ? funnelSteps[index - 1] : null;
            const dropoff = prevStep ? (((prevStep.value - step.value) / prevStep.value) * 100).toFixed(1) : 0;
            
            return (
              <div key={step.label} style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <div>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>{step.label}</span>
                    <span style={{ marginLeft: '12px', color: '#666', fontSize: '13px' }}>
                      {step.description}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: '700', fontSize: '16px' }}>
                      {step.value.toLocaleString()}
                    </span>
                    <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>
                      ({widthPercent.toFixed(1)}%)
                    </span>
                    {index > 0 && (
                      <span style={{ 
                        marginLeft: '12px', 
                        color: '#ef4444', 
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        ↓ {dropoff}% drop
                      </span>
                    )}
                  </div>
                </div>
                <div style={{
                  position: 'relative',
                  height: '40px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: `${Math.max(widthPercent, 5)}%`,
                    height: '100%',
                    background: step.color,
                    borderRadius: '8px',
                    transition: 'width 0.5s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {widthPercent > 15 && (
                      <span style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
                        {step.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Funnel Insights */}
        <div style={{ 
          marginTop: '24px', 
          padding: '16px', 
          background: '#fef3c7', 
          borderRadius: '8px',
          border: '1px solid #fbbf24'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: '#92400e' }}>
            💡 Key Insights
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#78350f', lineHeight: '1.8' }}>
            <li>
              <strong>{((funnel.usersWithGoals / funnel.totalUsers) * 100).toFixed(1)}%</strong> of users complete the onboarding and set a fitness goal
            </li>
            <li>
              <strong>{((funnel.usersWithFoodLog / funnel.usersWithGoals) * 100).toFixed(1)}%</strong> of users with goals go on to log their first meal
            </li>
            <li>
              <strong>{funnel.anonymousUsers.toLocaleString()}</strong> anonymous users could be converted to registered accounts
            </li>
            <li>
              Users take an average of <strong>{metrics.avgHoursToFirstLog} hours</strong> to log their first food after signing up
            </li>
          </ul>
        </div>
      </div>

      {/* Conversion by Acquisition Source */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title">🎯 Funnel by Acquisition Source</h2>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
          Compare how users from different sources progress through the funnel
        </p>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th>Signups</th>
                <th>Set Goal</th>
                <th>Goal Rate</th>
                <th>First Log</th>
                <th>Log Rate</th>
                <th>Activated</th>
                <th>Activation Rate</th>
              </tr>
            </thead>
            <tbody>
              {conversionBySource
                .filter(s => s.total_users > 0)
                .sort((a, b) => {
                  const rateA = (a.activated / a.total_users) || 0;
                  const rateB = (b.activated / b.total_users) || 0;
                  return rateB - rateA;
                })
                .map((source, idx) => {
                  const goalRate = ((source.set_goal / source.total_users) * 100).toFixed(1);
                  const logRate = ((source.logged_food / source.total_users) * 100).toFixed(1);
                  const activationRate = ((source.activated / source.total_users) * 100).toFixed(1);
                  
                  return (
                    <tr key={source.hear_about_us}>
                      <td>
                        <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {idx === 0 && '🥇'}
                          {idx === 1 && '🥈'}
                          {idx === 2 && '🥉'}
                          {source.source_name}
                        </strong>
                      </td>
                      <td>{source.total_users.toLocaleString()}</td>
                      <td>{source.set_goal.toLocaleString()}</td>
                      <td>
                        <span style={{ 
                          color: goalRate >= 50 ? '#10b981' : goalRate >= 30 ? '#f59e0b' : '#ef4444',
                          fontWeight: '600'
                        }}>
                          {goalRate}%
                        </span>
                      </td>
                      <td>{source.logged_food.toLocaleString()}</td>
                      <td>
                        <span style={{ 
                          color: logRate >= 30 ? '#10b981' : logRate >= 15 ? '#f59e0b' : '#ef4444',
                          fontWeight: '600'
                        }}>
                          {logRate}%
                        </span>
                      </td>
                      <td>{source.activated}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '60px',
                            height: '6px',
                            background: '#e5e7eb',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.min(activationRate * 2, 100)}%`,
                              height: '100%',
                              background: activationRate >= 10 ? '#10b981' : activationRate >= 5 ? '#f59e0b' : '#ef4444',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ 
                            fontWeight: '700',
                            color: activationRate >= 10 ? '#10b981' : activationRate >= 5 ? '#f59e0b' : '#ef4444'
                          }}>
                            {activationRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Weekly Cohorts */}
      <div className="card">
        <h2 className="card-title">📅 Weekly Cohort Performance</h2>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
          Track how recent signup cohorts are progressing through the funnel
        </p>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Cohort Week</th>
                <th>Signups</th>
                <th>Set Goal</th>
                <th>Goal %</th>
                <th>First Log</th>
                <th>Log %</th>
                <th>Activated</th>
                <th>Activation %</th>
              </tr>
            </thead>
            <tbody>
              {weeklyCohorts.map((cohort, idx) => {
                const goalPct = ((cohort.set_goal / cohort.signups) * 100).toFixed(1);
                const logPct = ((cohort.first_log / cohort.signups) * 100).toFixed(1);
                const activationPct = ((cohort.activated / cohort.signups) * 100).toFixed(1);
                
                return (
                  <tr key={cohort.cohort_week}>
                    <td>
                      <strong>
                        {idx === 0 && '📍 '}
                        Week of {formatDate(cohort.cohort_week)}
                      </strong>
                      {idx === 0 && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '11px', 
                          background: '#dbeafe', 
                          color: '#1d4ed8',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          Current
                        </span>
                      )}
                    </td>
                    <td>{cohort.signups}</td>
                    <td>{cohort.set_goal}</td>
                    <td style={{ color: goalPct >= 50 ? '#10b981' : '#666' }}>{goalPct}%</td>
                    <td>{cohort.first_log}</td>
                    <td style={{ color: logPct >= 30 ? '#10b981' : '#666' }}>{logPct}%</td>
                    <td>{cohort.activated}</td>
                    <td>
                      <strong style={{ 
                        color: activationPct >= 10 ? '#10b981' : activationPct >= 5 ? '#f59e0b' : '#ef4444'
                      }}>
                        {activationPct}%
                      </strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Recommendations */}
      <div className="card" style={{ marginTop: '24px', background: '#f0fdf4', border: '1px solid #10b981' }}>
        <h2 className="card-title" style={{ color: '#065f46' }}>🚀 Growth Opportunities</h2>
        <div style={{ lineHeight: '1.8', fontSize: '14px', color: '#047857' }}>
          <div style={{ marginBottom: '16px' }}>
            <strong style={{ color: '#065f46' }}>1. Reduce Time to First Log</strong>
            <p style={{ margin: '4px 0 0 0' }}>
              Users wait {metrics.avgHoursToFirstLog} hours on average. Add a guided first-meal prompt 
              during onboarding to get users logging immediately.
            </p>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <strong style={{ color: '#065f46' }}>2. Convert Anonymous Users</strong>
            <p style={{ margin: '4px 0 0 0' }}>
              {funnel.anonymousUsers.toLocaleString()} users are anonymous. Prompt registration 
              after 3 food logs with a "Save your progress" message.
            </p>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <strong style={{ color: '#065f46' }}>3. Boost Week 1 Activation</strong>
            <p style={{ margin: '4px 0 0 0' }}>
              Only {metrics.activationRate}% of users log 3+ times in week 1. 
              Send daily reminder notifications for the first 7 days.
            </p>
          </div>
          
          <div>
            <strong style={{ color: '#065f46' }}>4. Focus on High-Converting Sources</strong>
            <p style={{ margin: '4px 0 0 0' }}>
              {conversionBySource.length > 0 && (
                <>
                  <strong>{conversionBySource.sort((a, b) => 
                    (b.activated/b.total_users) - (a.activated/a.total_users)
                  )[0]?.source_name}</strong> has the best activation rate. 
                  Consider increasing marketing spend on this channel.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivationFunnel;


